import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { applyStaticMergeTags } from '@/utils/mergeTags';
import {
  Save,
  Plus,
  Edit2,
  FileText,
  AlertCircle,
  Trash2,
  Copy,
  Eye,
  Clock,
  CheckCircle2,
  XCircle,
  Globe,
  Lock,
} from 'lucide-react';
import { templateActivationCache } from '@/utils/templateActivationCache';

interface ScriptTemplate {
  id: string;
  name: string;
  category: string;
  description: string | null;
  content: string;
  first_message: string | null;
  created_at: string;
  is_active?: boolean;
  is_public?: boolean;
}

interface VersionHistory {
  id: string;
  name: string;
  created_at: string;
  is_active: boolean;
  is_public: boolean;
}

export function ScriptTemplatesManagement() {
  const { toast } = useToast();
  const [templates, setTemplates] = useState<ScriptTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] =
    useState<ScriptTemplate | null>(null);
  const [versionHistory, setVersionHistory] = useState<VersionHistory[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewData, setPreviewData] = useState({
    companyName: 'Acme Solutions',
    userName: 'Sarah',
    userTitle: 'Sales Consultant',
    industry: 'Technology Services',
    businessType: 'Software Development',
    hours: 'M–F 9 to 5.',
    location: 'We are remote and located in many areas.',
    services: '',
  });

  const [formData, setFormData] = useState({
    name: '',
    category: 'inbound',
    description: '',
    content: '',
    first_message: '',
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('script_templates')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Use bulk status fetch
      const templateIds = (data || []).map((template) => template.id);
      const { activationMap, publicMap } =
        await templateActivationCache.getTemplateStatuses(templateIds);

      const templatesWithStatus = (data || []).map((template) => ({
        ...template,
        is_active: activationMap[template.id] === true,
        is_public: publicMap[template.id] === true,
      }));

      setTemplates(templatesWithStatus);
    } catch (error) {
      console.error('Error fetching templates:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error occurred';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: 'Failed to fetch script templates: ' + errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const setTemplateActiveStatus = async (
    templateId: string,
    active: boolean
  ) => {
    try {
      const { error } = await supabase
        .from('system_settings')
        .update({
          setting_value: active.toString(),
        })
        .eq('setting_key', `template_active_${templateId}`);

      if (error) throw error;

      // Clear cache and refetch
      templateActivationCache.clear();
      await fetchTemplates();
    } catch (error) {
      console.error('Error updating template status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update template activation status',
        variant: 'destructive',
      });
    }
  };

  const setTemplatePublicStatus = async (
    templateId: string,
    isPublic: boolean
  ) => {
    try {
      const { error } = await supabase
        .from('system_settings')
        .update({
          is_public: isPublic,
        })
        .eq('setting_key', `template_active_${templateId}`);

      if (error) throw error;

      // Clear cache and refetch
      templateActivationCache.clear();
      await fetchTemplates();
    } catch (error) {
      console.error('Error updating template public status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update template public status',
        variant: 'destructive',
      });
    }
  };

  const fetchVersionHistory = async (templateBaseName: string) => {
    try {
      const { data, error } = await supabase
        .from('script_templates')
        .select('id, name, created_at')
        .ilike('name', `${templateBaseName}%`)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Use bulk status fetch for version history too
      const versionIds = (data || []).map((version) => version.id);
      const { activationMap, publicMap } =
        await templateActivationCache.getTemplateStatuses(versionIds);

      const versionsWithStatus = (data || []).map((version) => ({
        ...version,
        is_active: activationMap[version.id] === true,
        is_public: publicMap[version.id] === true,
      }));

      setVersionHistory(versionsWithStatus);
    } catch (error) {
      console.error('Error fetching version history:', error);
    }
  };

  const handleTemplateSelect = (template: ScriptTemplate) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      category: template.category,
      description: template.description || '',
      content: template.content,
      first_message: template.first_message || '',
    });
    setIsEditing(false);
  };

  const handleNewTemplate = () => {
    setSelectedTemplate(null);
    setFormData({
      name: '',
      category: 'inbound',
      description: '',
      content: '',
      first_message: '',
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.content.trim()) {
      toast({
        title: 'Error',
        description: 'Please fill in both name and content',
        variant: 'destructive',
      });
      return;
    }

    // Validate first message for inbound templates
    if (formData.category === 'inbound' && !formData.first_message?.trim()) {
      toast({
        title: 'Error',
        description: 'First message is required for inbound templates',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);

    try {
      let savedTemplate;
      if (selectedTemplate) {
        // Update existing template
        const { data, error } = await supabase
          .from('script_templates')
          .update({
            name: formData.name,
            category: formData.category,
            description: formData.description || null,
            content: formData.content,
            first_message: formData.first_message || null,
          })
          .eq('id', selectedTemplate.id)
          .select()
          .single();

        if (error) throw error;
        savedTemplate = data;

        toast({
          title: 'Success',
          description: 'Script template updated successfully',
        });
      } else {
        // Create new template
        const { data, error } = await supabase
          .from('script_templates')
          .insert({
            name: formData.name,
            category: formData.category,
            description: formData.description || null,
            content: formData.content,
            first_message: formData.first_message || null,
          })
          .select()
          .single();

        if (error) throw error;
        savedTemplate = data;

        // Create activation entry with active=true and is_public=false
        const { error: settingsError } = await supabase
          .from('system_settings')
          .insert({
            setting_key: `template_active_${savedTemplate.id}`,
            setting_value: 'true',
            description: `Activation status for template ${savedTemplate.id}`,
            setting_type: 'boolean',
            is_public: false,
          });

        if (settingsError) throw settingsError;

        toast({
          title: 'Success',
          description: 'Script template created successfully',
        });
      }

      await fetchTemplates();
      setSelectedTemplate(savedTemplate);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving template:', error);
      toast({
        title: 'Error',
        description: 'Failed to save script template',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDuplicate = async (template: ScriptTemplate) => {
    try {
      const { data, error } = await supabase
        .from('script_templates')
        .insert({
          name: `${template.name} (Copy)`,
          category: template.category,
          description: template.description,
          content: template.content,
          first_message: template.first_message,
        })
        .select()
        .single();

      if (error) throw error;

      // Create activation entry with active=true and is_public=false for duplicate
      const { error: settingsError } = await supabase
        .from('system_settings')
        .insert({
          setting_key: `template_active_${data.id}`,
          setting_value: 'true',
          description: `Activation status for template ${data.id}`,
          setting_type: 'boolean',
          is_public: false,
        });

      if (settingsError) throw settingsError;

      toast({
        title: 'Success',
        description: 'Template duplicated successfully',
      });

      await fetchTemplates();
    } catch (error) {
      console.error('Error duplicating template:', error);
      toast({
        title: 'Error',
        description: 'Failed to duplicate template',
        variant: 'destructive',
      });
    }
  };

  const handleSaveAsNewVersion = async (template: ScriptTemplate) => {
    try {
      const baseName = template.name.replace(/ v\d+$/, ''); // Remove existing version suffix
      const versionMatch = template.name.match(/ v(\d+)$/);
      const nextVersion = versionMatch ? parseInt(versionMatch[1]) + 1 : 2;

      const { data, error } = await supabase
        .from('script_templates')
        .insert({
          name: `${baseName} v${nextVersion}`,
          category: template.category,
          description: template.description,
          content: formData.content,
          first_message: formData.first_message,
        })
        .select()
        .single();

      if (error) throw error;

      // Create activation entry for new version with active=true and is_public=false
      const { error: newSettingsError } = await supabase
        .from('system_settings')
        .insert({
          setting_key: `template_active_${data.id}`,
          setting_value: 'true',
          description: `Activation status for template ${data.id}`,
          setting_type: 'boolean',
          is_public: false,
        });

      if (newSettingsError) throw newSettingsError;

      // Deactivate old version
      await setTemplateActiveStatus(template.id, false);

      toast({
        title: 'Success',
        description: 'New template version created successfully',
      });

      await fetchTemplates();
    } catch (error) {
      console.error('Error creating new version:', error);
      toast({
        title: 'Error',
        description: 'Failed to create new version',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (template: ScriptTemplate) => {
    if (!confirm(`Are you sure you want to delete "${template.name}"?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('script_templates')
        .delete()
        .eq('id', template.id);

      if (error) throw error;

      // Also remove activation setting
      await supabase
        .from('system_settings')
        .delete()
        .eq('setting_key', `template_active_${template.id}`);

      toast({
        title: 'Success',
        description: 'Template deleted successfully',
      });

      await fetchTemplates();
      setSelectedTemplate(null);
    } catch (error) {
      console.error('Error deleting template:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete template',
        variant: 'destructive',
      });
    }
  };

  const handleVersionSelect = async (versionId: string) => {
    try {
      const { data, error } = await supabase
        .from('script_templates')
        .select('*')
        .eq('id', versionId)
        .single();

      if (error) throw error;

      handleTemplateSelect(data);
    } catch (error) {
      console.error('Error selecting version:', error);
      toast({
        title: 'Error',
        description: 'Failed to load template version',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className='space-y-6'>
        <div className='flex justify-between items-center'>
          <h2 className='text-2xl font-bold'>Script Templates Management</h2>
        </div>
        <div className='flex items-center justify-center p-8'>
          <div className='text-center'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto'></div>
            <p className='mt-2 text-muted-foreground'>Loading templates...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='space-y-6'>
        <div className='flex justify-between items-center'>
          <h2 className='text-2xl font-bold'>Script Templates Management</h2>
          <Button onClick={fetchTemplates}>Retry</Button>
        </div>
        <Card className='border-destructive'>
          <CardContent className='pt-6'>
            <div className='flex items-center gap-2 text-destructive'>
              <AlertCircle className='h-5 w-5' />
              <p>Error loading templates: {error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div className='flex justify-between items-center'>
        <h2 className='text-2xl font-bold'>Script Templates Management</h2>
        <Button onClick={handleNewTemplate}>
          <Plus className='h-4 w-4 mr-2' />
          New Template
        </Button>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        {/* Templates List */}
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <FileText className='h-5 w-5' />
              Available Templates ({templates.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className='space-y-2'>
              {templates.map((template) => (
                <div
                  key={template.id}
                  className={`p-3 border rounded-lg cursor-pointer hover:bg-accent transition-colors ${
                    selectedTemplate?.id === template.id
                      ? 'border-primary bg-accent'
                      : ''
                  }`}
                  onClick={() => handleTemplateSelect(template)}
                >
                  <div className='flex justify-between items-start'>
                    <div className='flex-1'>
                      <div className='flex items-center gap-2 mb-1'>
                        <h3 className='font-medium'>{template.name}</h3>
                        <Badge
                          variant={
                            template.category === 'inbound'
                              ? 'default'
                              : 'secondary'
                          }
                        >
                          {template.category}
                        </Badge>
                        <Badge
                          variant={template.is_active ? 'default' : 'outline'}
                        >
                          {template.is_active ? (
                            <CheckCircle2 className='h-3 w-3 mr-1' />
                          ) : (
                            <XCircle className='h-3 w-3 mr-1' />
                          )}
                          {template.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                        <Switch
                          checked={template.is_active}
                          onCheckedChange={(checked) =>
                            setTemplateActiveStatus(template.id, checked)
                          }
                          aria-label='Toggle active'
                        />
                        <Badge
                          variant={template.is_public ? 'default' : 'outline'}
                        >
                          {template.is_public ? (
                            <Globe className='h-3 w-3 mr-1' />
                          ) : (
                            <Lock className='h-3 w-3 mr-1' />
                          )}
                          {template.is_public ? 'Public' : 'Private'}
                        </Badge>
                        <Switch
                          checked={template.is_public}
                          onCheckedChange={(checked) =>
                            setTemplatePublicStatus(template.id, checked)
                          }
                          aria-label='Toggle public'
                        />
                      </div>
                      <p className='text-sm text-muted-foreground'>
                        {new Date(template.created_at).toLocaleDateString()}
                      </p>
                      {template.description && (
                        <p className='text-sm text-muted-foreground mt-1'>
                          {template.description}
                        </p>
                      )}
                    </div>
                    <div className='flex items-center gap-2'>
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDuplicate(template);
                        }}
                      >
                        <Copy className='h-3 w-3' />
                      </Button>
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(template);
                        }}
                      >
                        <Trash2 className='h-3 w-3' />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              {templates.length === 0 && (
                <p className='text-center text-muted-foreground py-4'>
                  No templates found. Click "New Template" to create one.
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Template Editor */}
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <Edit2 className='h-5 w-5' />
              {selectedTemplate ? 'Edit Template' : 'New Template'}
            </CardTitle>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div>
              <Label htmlFor='templateName'>Template Name *</Label>
              <Input
                id='templateName'
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder='e.g., Warm & Conversational'
                disabled={!isEditing && !!selectedTemplate}
              />
            </div>

            <div>
              <Label htmlFor='category'>Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, category: value }))
                }
                disabled={!isEditing && !!selectedTemplate}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='inbound'>Inbound</SelectItem>
                  <SelectItem value='outbound'>Outbound</SelectItem>
                  <SelectItem value='general'>General</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor='description'>Description</Label>
              <Input
                id='description'
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder='Brief description of this template'
                disabled={!isEditing && !!selectedTemplate}
              />
            </div>

            {/* First Message for Inbound Templates */}
            {formData.category === 'inbound' && (
              <div>
                <Label htmlFor='first_message'>
                  First Message (Greeting) *
                </Label>
                <Input
                  id='first_message'
                  value={formData.first_message}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      first_message: e.target.value,
                    }))
                  }
                  placeholder='e.g., Hello! Thanks for calling {{companyName}}. How can I help you today?'
                  disabled={!isEditing && !!selectedTemplate}
                />
                <p className='text-sm text-muted-foreground mt-1'>
                  The first spoken line when someone calls. Use merge tags like{' '}
                  {'{{companyName}}'} for dynamic content.
                </p>
              </div>
            )}

            {/* Merge Tag Preview Toggle */}
            {selectedTemplate && (
              <div className='flex items-center gap-2'>
                <Switch
                  id='previewMode'
                  checked={previewMode}
                  onCheckedChange={setPreviewMode}
                />
                <Label htmlFor='previewMode' className='text-sm'>
                  Show merge tag preview
                </Label>
              </div>
            )}

            {/* Preview Data Form */}
            {previewMode && selectedTemplate && (
              <Card className='p-4 bg-muted/50'>
                <h4 className='font-medium mb-3 flex items-center gap-2'>
                  <Eye className='h-4 w-4' />
                  Preview Data
                </h4>
                <div className='grid grid-cols-2 gap-3'>
                  <div>
                    <Label htmlFor='previewCompanyName' className='text-xs'>
                      Company Name
                    </Label>
                    <Input
                      id='previewCompanyName'
                      value={previewData.companyName}
                      onChange={(e) =>
                        setPreviewData((prev) => ({
                          ...prev,
                          companyName: e.target.value,
                        }))
                      }
                      className='h-8 text-sm'
                    />
                  </div>
                  <div>
                    <Label htmlFor='previewUserName' className='text-xs'>
                      User Name
                    </Label>
                    <Input
                      id='previewUserName'
                      value={previewData.userName}
                      onChange={(e) =>
                        setPreviewData((prev) => ({
                          ...prev,
                          userName: e.target.value,
                        }))
                      }
                      className='h-8 text-sm'
                    />
                  </div>
                  <div>
                    <Label htmlFor='previewUserTitle' className='text-xs'>
                      User Title
                    </Label>
                    <Input
                      id='previewUserTitle'
                      value={previewData.userTitle}
                      onChange={(e) =>
                        setPreviewData((prev) => ({
                          ...prev,
                          userTitle: e.target.value,
                        }))
                      }
                      className='h-8 text-sm'
                    />
                  </div>
                  <div>
                    <Label htmlFor='previewIndustry' className='text-xs'>
                      Industry
                    </Label>
                    <Input
                      id='previewIndustry'
                      value={previewData.industry}
                      onChange={(e) =>
                        setPreviewData((prev) => ({
                          ...prev,
                          industry: e.target.value,
                        }))
                      }
                      className='h-8 text-sm'
                    />
                  </div>
                  {/* Add New Custom Fields below */}
                  <div>
                    <Label className='text-xs'>Business Hours</Label>
                    <Input
                      value={previewData.hours || ''}
                      onChange={(e) =>
                        setPreviewData((prev) => ({
                          ...prev,
                          hours: e.target.value,
                        }))
                      }
                      className='h-8 text-sm'
                      placeholder='Mon–Fri 9AM–6PM'
                    />
                  </div>
                  <div>
                    <Label className='text-xs'>Location</Label>
                    <Input
                      value={previewData.location || ''}
                      onChange={(e) =>
                        setPreviewData((prev) => ({
                          ...prev,
                          location: e.target.value,
                        }))
                      }
                      className='h-8 text-sm'
                      placeholder='Austin, TX'
                    />
                  </div>
                  <div className='col-span-2'>
                    <Label className='text-xs'>Services</Label>
                    <Input
                      value={previewData.services || ''}
                      onChange={(e) =>
                        setPreviewData((prev) => ({
                          ...prev,
                          services: e.target.value,
                        }))
                      }
                      className='h-8 text-sm'
                      placeholder='Roofing, HVAC, Electrical'
                    />
                  </div>
                </div>
              </Card>
            )}

            <div>
              <Label htmlFor='content'>Template Content *</Label>
              <textarea
                id='content'
                value={
                  previewMode && selectedTemplate
                    ? applyStaticMergeTags(formData.content, previewData)
                    : formData.content
                }
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, content: e.target.value }))
                }
                className='w-full h-[40vh] p-4 border rounded-lg font-mono text-sm resize-none'
                placeholder='Enter the script template content...'
                disabled={(!isEditing && !!selectedTemplate) || previewMode}
              />
              <p className='text-sm text-muted-foreground mt-1'>
                Use merge tags like {'{{companyName}}'}, {'{{userName}}'},{' '}
                {'{{industry}}'} for dynamic content
                {previewMode && ' • Preview mode shows resolved merge tags'}
              </p>
            </div>

            {/* Version History */}
            {selectedTemplate && !isEditing && (
              <Card className='p-4 bg-muted/50'>
                <h4 className='font-medium mb-3 flex items-center gap-2'>
                  <Clock className='h-4 w-4' />
                  Version History
                </h4>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() =>
                    fetchVersionHistory(
                      selectedTemplate.name.replace(/ v\d+$/, '')
                    )
                  }
                  className='mb-3'
                >
                  Load Versions
                </Button>
                {versionHistory.length > 0 && (
                  <div className='space-y-2'>
                    {versionHistory.map((version) => (
                      <div
                        key={version.id}
                        className={`p-2 border rounded cursor-pointer text-sm ${
                          version.id === selectedTemplate.id
                            ? 'border-primary bg-accent'
                            : 'hover:bg-accent'
                        }`}
                        onClick={() => handleVersionSelect(version.id)}
                      >
                        <div className='flex justify-between items-center'>
                          <span>{version.name}</span>
                          <div className='flex items-center gap-2'>
                            <Badge
                              variant={
                                version.is_active ? 'default' : 'outline'
                              }
                              className='text-xs'
                            >
                              {version.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                            <Badge
                              variant={
                                version.is_public ? 'default' : 'outline'
                              }
                              className='text-xs'
                            >
                              {version.is_public ? 'Public' : 'Private'}
                            </Badge>
                            <span className='text-muted-foreground text-xs'>
                              {new Date(
                                version.created_at
                              ).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            )}

            {/* Action Buttons */}
            {selectedTemplate && !isEditing && (
              <div className='flex gap-2'>
                <Button
                  variant='outline'
                  onClick={() => setIsEditing(true)}
                  className='flex-1'
                >
                  <Edit2 className='h-4 w-4 mr-2' />
                  Edit
                </Button>
                <Button
                  variant='outline'
                  onClick={() => handleDuplicate(selectedTemplate)}
                >
                  <Copy className='h-4 w-4 mr-2' />
                  Duplicate
                </Button>
                <Button
                  variant='outline'
                  onClick={() => handleDelete(selectedTemplate)}
                >
                  <Trash2 className='h-4 w-4 mr-2' />
                  Delete
                </Button>
              </div>
            )}

            {(isEditing || !selectedTemplate) && (
              <div className='flex gap-2'>
                <Button
                  onClick={handleSave}
                  disabled={
                    isSaving ||
                    !formData.name.trim() ||
                    !formData.content.trim()
                  }
                  className='flex-1'
                >
                  <Save className='h-4 w-4 mr-2' />
                  {isSaving
                    ? 'Saving...'
                    : selectedTemplate
                    ? 'Update Template'
                    : 'Create Template'}
                </Button>
                {selectedTemplate && (
                  <Button
                    variant='outline'
                    onClick={() => handleSaveAsNewVersion(selectedTemplate)}
                    disabled={isSaving}
                  >
                    <Plus className='h-4 w-4 mr-2' />
                    Save as New Version
                  </Button>
                )}
                {selectedTemplate && (
                  <Button
                    variant='outline'
                    onClick={() => setIsEditing(false)}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
